import React, { useState } from "react";
import { X, Gift, ArrowRight, Copy, Check } from "lucide-react";
import { setFlag, SUBSCRIBED_FLAG } from "../utils/preferences";
import { identify } from "../utils/klaviyo";
import TermsCheckbox from "./TermsCheckbox";

// Leads go to the middleware, which issues a 10% code locked to this email address
const MIDDLEWARE_URL = (process.env.REACT_APP_MIDDLEWARE_URL || "https://krunchmate-middleware.onrender.com").replace(/\/+$/, "");

// localStorage can throw in private browsing or with site data blocked
const remember = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // the code is still shown on screen
  }
};

export default function LeadCaptureModal({ isOpen, onClose, onSuccess, triggerSource = "popup" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !name.trim()) {
      setError("Please fill in both your name and email address.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Terms & Conditions to claim your code.");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${MIDDLEWARE_URL}/api/lead-capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), source: triggerSource }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && typeof data.coupon === "string" && data.coupon) {
        remember("km_lead_name", name.trim());
        remember("km_lead_email", email.trim());
        remember("km_welcome_coupon", data.coupon);
        // They have their code, so the footer stops offering it
        setFlag(SUBSCRIBED_FLAG);
        identify(email.trim());
        setCouponCode(data.coupon);
      } else {
        setError(
          typeof data.error === "string" && data.error.length <= 200
            ? data.error
            : "We couldn't create your code right now. Please try again."
        );
      }
    } catch (err) {
      console.error("Lead capture error:", err);
      setError("We couldn't reach the server. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setCopied(true);
    } catch {
      // clipboard unavailable; the code is visible to copy by hand
    }
  };

  const handleContinue = () => {
    if (onSuccess) onSuccess({ name: name.trim(), email: email.trim(), coupon: couponCode });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-capture-title"
        className="relative w-full max-w-md bg-[#082D33] border border-[#FFFBE5]/20 rounded-3xl p-6 lg:p-8 text-[#FFFBE5] shadow-2xl space-y-6"
      >
        <button
          type="button"
          onClick={couponCode ? handleContinue : onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 text-[#FFFBE5]/60 hover:text-[#FFFBE5] transition-colors"
        >
          <X size={20} />
        </button>

        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center p-3 bg-[#F6A81E]/20 text-[#F6A81E] rounded-2xl mb-2">
            <Gift size={28} />
          </div>
          <h2 id="lead-capture-title" className="text-2xl lg:text-3xl font-display uppercase tracking-wider text-[#F6A81E]">
            {couponCode
              ? "Your 10% Code"
              : triggerSource === "cart" ? "Unlock 10% Off Your Order!" : "Join KrunchMate Rewards"}
          </h2>
          <p className="text-sm opacity-80 leading-relaxed">
            {couponCode
              ? "Enter this code at checkout with the same email address. It works once."
              : "Enter your details to claim your discount code and track your order updates seamlessly."}
          </p>
        </div>

        {couponCode ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 p-4 bg-gradient-to-br from-[#F6A81E]/20 to-transparent border border-[#F6A81E]/40 rounded-2xl">
              <span className="font-display text-xl tracking-widest text-[#F6A81E] break-all">{couponCode}</span>
              <button
                type="button"
                onClick={handleCopy}
                aria-label="Copy discount code"
                className="shrink-0 p-2 rounded-lg bg-[#FFFBE5]/10 hover:bg-[#FFFBE5]/20 transition-colors"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <button
              type="button"
              onClick={handleContinue}
              className="w-full py-4 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#f7b43b] transition-colors shadow-lg"
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div role="alert" className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-xs text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="lead-name" className="block text-xs uppercase tracking-widest opacity-60 mb-1.5">First Name</label>
                <input
                  id="lead-name"
                  type="text"
                  required
                  maxLength={50}
                  autoComplete="given-name"
                  placeholder="e.g. Alex"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3.5 bg-black/30 border border-[#FFFBE5]/20 rounded-xl outline-none focus:border-[#F6A81E] text-[#FFFBE5] placeholder-[#FFFBE5]/30 text-sm"
                />
              </div>
              <div>
                <label htmlFor="lead-email" className="block text-xs uppercase tracking-widest opacity-60 mb-1.5">Email Address</label>
                <input
                  id="lead-email"
                  type="email"
                  required
                  maxLength={254}
                  autoComplete="email"
                  placeholder="alex@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3.5 bg-black/30 border border-[#FFFBE5]/20 rounded-xl outline-none focus:border-[#F6A81E] text-[#FFFBE5] placeholder-[#FFFBE5]/30 text-sm"
                />
              </div>
              <TermsCheckbox id="lead-terms" checked={agreedToTerms} onChange={setAgreedToTerms} />
              <button
                type="submit"
                disabled={isSubmitting || !agreedToTerms}
                className="w-full py-4 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#f7b43b] transition-colors shadow-lg mt-2 disabled:opacity-50"
              >
                {isSubmitting ? "Saving..." : "Claim Discount & Continue"} <ArrowRight size={16} />
              </button>
            </form>
            <p className="text-[10px] text-center opacity-50 uppercase tracking-widest">
              No spam, ever. Unsubscribe at any time.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
